// @ts-strict-ignore
import * as React from 'react';
import Tooltip from 'antd/lib/tooltip';
import PermissionedActionButton from './../HOCs/PermissionedActionButton';
import { CheckCircleOutlined, StopOutlined } from '@ant-design/icons';
import { SubscriptionFeatureType, UserGroup } from '../../types';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import SubscriptionPlanIcon from '../planRestrictions/SubscriptionPlanIcon';
import { antGrey } from './../styles/globalStyles';
import { useDispatch } from 'react-redux';
import { OrgSettingsUpdatable } from '@/actions/updatable';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { SetDefaultGroupsDisabledRequest, tryUpdateOrgSettings } from '@/actions/orgSettings';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface DisableGroupButtonProps {
    selectedGroup: UserGroup;
    orgId: string;
    onToggled: () => void;
}

const DisableGroupButton: React.FC<DisableGroupButtonProps> = (props) => {
    const { selectedGroup, orgId } = props;
    const dispatch = useDispatch();
    const isError = useError(OrgSettingsUpdatable.SetDefaultGroupsDisabled, selectedGroup.id);
    const isLoading = useIsLoading(OrgSettingsUpdatable.SetDefaultGroupsDisabled, selectedGroup.id);
    const isSuccess = useUpdated(OrgSettingsUpdatable.SetDefaultGroupsDisabled, selectedGroup.id);

    const isDisabled = !!selectedGroup.disabled;
    const memberCount = selectedGroup.members?.length ?? 0;
    // Only block the disable direction on members; enabling is always allowed.
    const blockedByMembers = !isDisabled && memberCount > 0;

    const toggleDisabled = () => {
        const request: SetDefaultGroupsDisabledRequest = {
            orgId,
            groupIds: [selectedGroup.id],
            disabled: !isDisabled,
        };
        dispatch(tryUpdateOrgSettings(OrgSettingsUpdatable.SetDefaultGroupsDisabled, request, selectedGroup.id));
    };

    React.useEffect(() => {
        if (isError) {
            globalMessage.error(isError);
        }
    }, [isError]);

    React.useEffect(() => {
        if (isSuccess) {
            globalMessage.success(isDisabled ? 'Group enabled' : 'Group disabled');
            props.onToggled();
        }
    }, [isSuccess]);

    const renderButton = (isPlanRestricted: boolean) => {
        const button = (
            <PermissionedActionButton
                type="link"
                icon={
                    isPlanRestricted ? <SubscriptionPlanIcon mini />
                    : isDisabled ?
                        <CheckCircleOutlined style={{ color: antGrey.grey7 }} />
                    :   <StopOutlined style={{ color: antGrey.grey7 }} />
                }
                style={{ color: antGrey.grey10, display: 'flex', alignItems: 'center' }}
                actionType={RunwayUserAction.UACreateEditDeleteCustomGroup}
                onClick={toggleDisabled}
                loading={isLoading}
                disabled={blockedByMembers || isPlanRestricted}
            >
                {isDisabled ? 'Enable group' : 'Disable group'}
            </PermissionedActionButton>
        );

        if (blockedByMembers && !isPlanRestricted) {
            return (
                <Tooltip title="Reassign this group's members before disabling it" placement="left">
                    <span>{button}</span>
                </Tooltip>
            );
        }

        return button;
    };

    return (
        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.CustomGroups} requiresTooltip tooltipNoIcon>
            {renderButton}
        </PlanRestrictedFeatureWrapper>
    );
};

export default DisableGroupButton;
